import map from 'lodash/map'
import groupBy from 'lodash/groupBy'
import partition from 'lodash/partition'
import toPairs from 'lodash/toPairs'
import * as React from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faCircleNotch,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons'
import {type NonEmptyArray} from '@freckle/non-empty'
import {H3} from '@frontrow/console/src/components/typography'
import {Button} from '@frontrow/console/src/components/button'
import {ExpandChevronButton} from '@frontrow/console/src/components/expand-chevron-button'
import {type ApiErrorT} from '@frontrow/console/src/entities/models/api-error'
import {exhaustive} from '@freckle/exhaustive'
import {type OperationLocationT} from '@frontrow/console/src/entities/helpers/operation'
import {type CallbackFn} from '@freckle/react-hooks'
import {mapMaybes} from '@freckle/maybe'
import {
  type GroupedArtifactData,
  allArtifactTags
} from '@frontrow/console/src/entities/models/artifact'

import {type CsvUploadStatusT} from '@frontrow/console/src/hooks/use-student-csv-upload'
import {
  type StudentLevelInfoT,
  type StudentLevelErrorT
} from '@frontrow/console/src/models/student-level-info'
import CsvStudentLevelPreviewTable from '@frontrow/console/src/components/pages/students-page/csv-student-level-preview/csv-student-level-preview-table'
import {PendingOperationWhenLocation} from '@frontrow/console/src/components/pending-operation'

type Props = {
  studentLevelData: StudentLevelInfoT | undefined | null
  warnings: Array<StudentLevelErrorT> | undefined | null
  csvPreviewParsingError: NonEmptyArray<ApiErrorT> | undefined | null
  csvUpdateError: string | undefined | null
  onUpdate: () => void
  updateStatus: CsvUploadStatusT
  operationLocation: OperationLocationT | undefined | null
  updateStudentLevelsSuccess: CallbackFn<(a: GroupedArtifactData) => void>
  updateStudentLevelsError: CallbackFn<() => void>
  artifactData: GroupedArtifactData | undefined | null
}

export default function CsvStudentLevelPreview(props: Props): React.ReactElement {
  const {
    studentLevelData,
    csvPreviewParsingError,
    warnings,
    onUpdate,
    updateStatus,
    csvUpdateError,
    operationLocation,
    updateStudentLevelsSuccess,
    updateStudentLevelsError,
    artifactData
  } = props

  const {message, iconElement, errorsElement, successElement} = (() => {
    switch (updateStatus) {
      case 'sending-file-for-preview':
        return {
          message: null,
          iconElement: null,
          errorsElement: null,
          successElement: null
        }

      case 'running-operation':
      case 'sending-file-for-update':
        return {
          message: 'Updating..',
          iconElement: <FontAwesomeIcon icon={faCircleNotch} spin />,
          errorsElement: null,
          successElement: null
        }

      case 'preview-parsing-success':
        return {
          message: 'No errors found. Confirm Student Levels to be Set',
          iconElement: <FontAwesomeIcon icon={faCheck} />,
          errorsElement: null,
          successElement: null
        }

      case 'parsing-failure':
        return {
          message: 'CSV file contains errors',
          iconElement: <FontAwesomeIcon icon={faExclamationTriangle} />,
          errorsElement:
            csvPreviewParsingError !== null && csvPreviewParsingError !== undefined ? (
              <CsvParsingErrors csvPreviewParsingError={csvPreviewParsingError} />
            ) : null,
          successElement: null
        }

      case 'update-success':
        return fromArtifactData(artifactData)

      case 'update-failure':
        return {
          message: 'Update Failure',
          iconElement: <FontAwesomeIcon icon={faExclamationTriangle} />,
          errorsElement: <p>{csvUpdateError}</p>,
          successElement: null
        }

      case 'no-preview-available':
        return {
          message: null,
          iconElement: null,
          errorsElement: null,
          successElement: null
        }

      default:
        return exhaustive(updateStatus)
    }
  })()

  const groupedWarnings = groupBy(warnings || [], x => x.name)

  const warningMessages = map(Object.keys(groupedWarnings), name => (
    <LogGroup
      key={name}
      name={name as StudentLevelErrorT['name']}
      grouped={groupedWarnings[name]}
    />
  ))

  return (
    <div className="block">
      {message !== null && message !== undefined && (
        <div className="my-10">
          <div className="flex items-center">
            {iconElement}
            <H3>{message}</H3>
          </div>
          {errorsElement !== null && errorsElement !== undefined ? (
            <React.Fragment>
              {errorsElement}
              <p>Please correct these errors in the CSV, then try and upload the file again.</p>
            </React.Fragment>
          ) : null}
        </div>
      )}
      {warningMessages}
      {successElement}
      {studentLevelData !== null && studentLevelData !== undefined && (
        <div className="my-10">
          <CsvStudentLevelPreviewTable
            isLoading={updateStatus === 'sending-file-for-preview'}
            studentLevelData={studentLevelData}
          />
        </div>
      )}
      {(() => {
        switch (updateStatus) {
          case 'preview-parsing-success':
          case 'no-preview-available':
            return <Button onClick={onUpdate}>Update Student Levels</Button>
          case 'parsing-failure':
          case 'sending-file-for-preview':
          case 'sending-file-for-update':
          case 'update-success':
          case 'update-failure':
          case 'running-operation':
            return null
          default:
            return exhaustive(updateStatus)
        }
      })()}
      <PendingOperationWhenLocation
        operationLocation={operationLocation}
        title={'Updating Student Levels'}
        heading={'Student levels are currently being updated.'}
        body={
          'This may take a few minutes to complete depending on site traffic. Please do not leave or refresh this page until the process is complete.'
        }
        onSuccess={updateStudentLevelsSuccess}
        onFailure={updateStudentLevelsError}
      />
    </div>
  )
}

function getWarningTitle(name: StudentLevelErrorT['name']): string {
  switch (name) {
    case 'math_star_placement_invalid_student':
      return 'Invalid Students'
    case 'math_star_placement_invalid_star_score':
      return 'Invalid Scores'
    case 'math_star_placement_student_was_previously_placed':
      return 'Students Previously Placed'
    default:
      // Catch all for when the backend introduces new error types
      return 'Various Errors'
  }
}

function LogGroup(props: {
  name: StudentLevelErrorT['name']
  grouped: Array<StudentLevelErrorT>
}): React.ReactElement {
  const [open, setOpen] = React.useState(false)
  const onToggle = () => setOpen(prev => !prev)
  return (
    <React.Fragment>
      <H3>
        <Button onClick={onToggle} style="link">
          <ExpandChevronButton expand={open} onClick={onToggle} />
          &nbsp;
          <FontAwesomeIcon icon={faExclamationTriangle} />
          &nbsp;
          {props.grouped.length} {getWarningTitle(props.name)}
        </Button>
      </H3>
      {open ? (
        <ul>
          {map(props.grouped, x => (
            <li>{x.message}</li>
          ))}
        </ul>
      ) : null}
    </React.Fragment>
  )
}

type SkippedStudents = GroupedArtifactData['sync_skipped_students']

function getSkipReason(reason: string): string {
  switch (reason) {
    case 'not_enrolled_in_course':
      return 'not enrolled in a course'
    default:
      // Catch all for when the backend introduces new skip reasons
      return 'unknown reason'
  }
}

function SkippedStudentsGroup(props: {skipped: SkippedStudents}): React.ReactElement {
  const {skipped} = props
  const [open, setOpen] = React.useState(false)
  const onToggle = () => setOpen(prev => !prev)
  return (
    <React.Fragment>
      <H3>
        <Button onClick={onToggle} style="link">
          <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
          &nbsp;
          <FontAwesomeIcon icon={faExclamationTriangle} />
          &nbsp; Skipped {skipped.length} {skipped.length === 1 ? 'student' : 'students'}
        </Button>
      </H3>
      {open && (
        <ul>
          {map(skipped, (student, i: number) => (
            <li key={i}>
              {student.sisId !== null && student.sisId !== undefined
                ? `SIS ID ${student.sisId}`
                : `Student ID ${student.studentId}`}
              : {getSkipReason(student.reason)}
            </li>
          ))}
        </ul>
      )}
    </React.Fragment>
  )
}

function CsvParsingErrors(props: {
  csvPreviewParsingError: NonEmptyArray<ApiErrorT>
}): React.ReactElement {
  const {csvPreviewParsingError} = props
  const [errorRows, genericErrors] = partition(
    csvPreviewParsingError,
    ({details}) => details !== null && details !== undefined && details.rowNumber !== undefined
  )
  const groupedRows = groupBy(errorRows, ({details}) => details?.rowNumber)
  return (
    <ul>
      {map(genericErrors, (error, i: number) => (
        <li key={i}>{error.message}</li>
      ))}
      {map(toPairs(groupedRows), ([rowNumber, errors], i: number) => (
        <li key={i}>
          <p>Row {rowNumber}:</p>
          <ul>
            {map(errors, (error, i: number) => (
              <li key={i}>{error.message}</li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

function fromArtifactData(groupedArtifactData?: GroupedArtifactData | null): {
  message: string
  successElement: React.ReactNode
  iconElement: React.ReactNode
  errorsElement: React.ReactNode
} {
  if (groupedArtifactData === null || groupedArtifactData === undefined) {
    return {
      message: 'Successfully Updated Student Levels!',
      iconElement: <FontAwesomeIcon icon={faCheck} />,
      errorsElement: null,
      successElement: null
    }
  }

  const errorsElement = mapMaybes(allArtifactTags, artifactTag => {
    switch (artifactTag) {
      case 'sync_fast_bridge_math_errors':
      case 'sync_fast_bridge_errors': {
        const errors = groupedArtifactData['sync_fast_bridge_errors']
        if (errors.length === 0) return null
        return (
          <ul key={artifactTag}>
            {map(errors, (data, i: number) => (
              <li key={i}>{data.message}</li>
            ))}
          </ul>
        )
      }
      case 'sync_ngmt_errors': {
        const errors = groupedArtifactData['sync_ngmt_errors']
        if (errors.length === 0) return null
        return (
          <ul key={artifactTag}>
            {map(errors, (data, i: number) => (
              <li key={i}>{data.message}</li>
            ))}
          </ul>
        )
      }
      case 'sync_m_a_p_errors': {
        const errors = groupedArtifactData['sync_m_a_p_errors']
        if (errors.length === 0) return null
        return (
          <ul key={artifactTag}>
            {map(errors, (data, i: number) => (
              <li key={i}>{data.message}</li>
            ))}
          </ul>
        )
      }
      case 'sync_fast_bridge_math_placements':
      case 'sync_fast_bridge_ela_placements':
      case 'sync_ngmt_placements':
      case 'sync_nwea_map_math_placements':
      case 'sync_nwea_map_ela_placements':
      case 'sync_m_a_p_math_placements':
      case 'sync_m_a_p_ela_placements':
      case 'sync_skipped_students':
        return null
      default:
        return exhaustive(artifactTag as never)
    }
  })

  const successElement = mapMaybes(allArtifactTags, artifactTag => {
    switch (artifactTag) {
      case 'sync_fast_bridge_math_placements':
      case 'sync_ngmt_placements':
      case 'sync_nwea_map_math_placements':
      case 'sync_m_a_p_math_placements':
        const mathData = groupedArtifactData[artifactTag]
        if (mathData.length === 0) return null
        return (
          <>
            <h3>Successful Placements</h3>
            <CsvStudentLevelPreviewTable
              isLoading={false}
              studentLevelData={{
                uploadType: 'Math',
                mathData
              }}
            />
          </>
        )
      case 'sync_fast_bridge_ela_placements':
      case 'sync_nwea_map_ela_placements':
      case 'sync_m_a_p_ela_placements':
        const elaData = groupedArtifactData[artifactTag]
        if (elaData.length === 0) return null
        return (
          <>
            <h3>Successful Placements</h3>
            <CsvStudentLevelPreviewTable
              isLoading={false}
              studentLevelData={{
                uploadType: 'Ela',
                elaData
              }}
            />
          </>
        )
      case 'sync_fast_bridge_math_errors':
      case 'sync_fast_bridge_errors':
      case 'sync_ngmt_errors':
      case 'sync_m_a_p_errors':
        return null
      case 'sync_skipped_students': {
        const skipped = groupedArtifactData['sync_skipped_students']
        if (skipped.length === 0) return null
        return <SkippedStudentsGroup key={artifactTag} skipped={skipped} />
      }
      default:
        return exhaustive(artifactTag as never)
    }
  })

  const skippedCount = groupedArtifactData['sync_skipped_students'].length

  return {
    message:
      errorsElement.length > 0
        ? successElement.length > 0 && skippedCount < successElement.length
          ? 'Some placements were successful but others had errors.'
          : 'Errors found when updating student levels.'
        : skippedCount > 0
          ? `Student levels updated. ${skippedCount} ${skippedCount === 1 ? 'student was' : 'students were'} skipped.`
          : 'Successfully Updated Student Levels!',
    iconElement:
      errorsElement.length > 0 || skippedCount > 0 ? (
        <FontAwesomeIcon icon={faExclamationTriangle} />
      ) : (
        <FontAwesomeIcon icon={faCheck} />
      ),
    errorsElement: errorsElement.length > 0 ? errorsElement : null,
    successElement: successElement.length > 0 ? successElement : null
  }
}
